import React, { useState, useMemo } from "react";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import { UserData } from "@/entities/users/types";
import { useUsersStats } from "@/entities/users/hooks";
import { useUserSelect } from "@/entities/users/useUserSelect";
import { useTranslation } from "react-i18next";
import RecordTransferRow from "./RecordTransferRow";
import { TransferData } from "./hooks/types";

const TRANSLATION_KEY = "settings.userManagement.bulkDeactivateUsersModal";

interface BulkDeactivateUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUsers: UserData[];
  onDeactivate: (transferData: TransferData) => void;
}

const BulkDeactivateUsersModal: React.FC<BulkDeactivateUsersModalProps> = ({
  isOpen,
  onClose,
  selectedUsers,
  onDeactivate
}) => {
  const { t } = useTranslation();
  const {
    options: userOptions,
    isLoading: isLoadingUsers,
    isOpen: isManagerOpen,
    handleOpenChange: handleManagerOpenChange,
    setSearchTerm: setManagerSearchTerm,
    renderUserIcon
  } = useUserSelect();
  const selectedUserIds = selectedUsers.map((user) => user.id);
  const aggregatedStats = useUsersStats(selectedUserIds);
  const [transferData, setTransferData] = useState<TransferData>({
    companies: null,
    deals: null,
    leads: null,
    newReportingManager: null
  });

  const recordCounts = useMemo(() => {
    const totalDirectReports = selectedUsers.reduce(
      (total, user) => total + (user.directReports?.length || 0),
      0
    );

    return {
      companies: aggregatedStats.companies || 0,
      deals: aggregatedStats.deals || 0,
      leads: aggregatedStats.leads || 0,
      directReports: totalDirectReports
    };
  }, [selectedUsers, aggregatedStats]);

  const availableUsers = useMemo(() => {
    const excludedIds = new Set(selectedUsers.map((user) => user.id));
    return userOptions.filter((option) => !excludedIds.has(String(option.id)));
  }, [userOptions, selectedUsers]);

  const isFormValid = useMemo(() => {
    const hasActiveRecords =
      recordCounts.companies > 0 ||
      recordCounts.deals > 0 ||
      recordCounts.leads > 0;
    const hasDirectReports = recordCounts.directReports > 0;

    if (hasActiveRecords) {
      const recordTransfersValid =
        (recordCounts.companies === 0 || transferData.companies) &&
        (recordCounts.deals === 0 || transferData.deals) &&
        (recordCounts.leads === 0 || transferData.leads);

      if (!recordTransfersValid) return false;
    }

    if (hasDirectReports && !transferData.newReportingManager) {
      return false;
    }

    return true;
  }, [recordCounts, transferData]);

  const handleDeactivate = () => {
    if (isFormValid) {
      onDeactivate(transferData);
      onClose();
    }
  };

  const handleTransferChange = (field: keyof TransferData, value: string) => {
    setTransferData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const hasActiveRecords =
    recordCounts.companies > 0 ||
    recordCounts.deals > 0 ||
    recordCounts.leads > 0;
  const hasDirectReports = recordCounts.directReports > 0;

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onClose()}
      title={
        <Text variant="title3H1">
          {t(`${TRANSLATION_KEY}.title`, { count: selectedUsers.length })}
        </Text>
      }
      content={
        <div className="flex flex-col gap-6 p-6">
          <Text variant="body">{t(`${TRANSLATION_KEY}.description`)}</Text>
          {hasActiveRecords && (
            <div className="flex flex-col gap-4">
              <Text variant="body">
                {t(`${TRANSLATION_KEY}.activeRecordsText`)}
              </Text>
              <div className="flex flex-col gap-4">
                <RecordTransferRow
                  type="companies"
                  label={t(`${TRANSLATION_KEY}.companies`)}
                  count={recordCounts.companies}
                  selectedValue={transferData.companies}
                  onTransferChange={handleTransferChange}
                  t={t}
                  translationKey={TRANSLATION_KEY}
                />
                <RecordTransferRow
                  type="deals"
                  label={t(`${TRANSLATION_KEY}.deals`)}
                  count={recordCounts.deals}
                  selectedValue={transferData.deals}
                  onTransferChange={handleTransferChange}
                  t={t}
                  translationKey={TRANSLATION_KEY}
                />
                <RecordTransferRow
                  type="leads"
                  label={t(`${TRANSLATION_KEY}.leads`)}
                  count={recordCounts.leads}
                  selectedValue={transferData.leads}
                  onTransferChange={handleTransferChange}
                  t={t}
                  translationKey={TRANSLATION_KEY}
                />
              </div>
            </div>
          )}
          {hasDirectReports && (
            <div className="flex flex-col gap-4">
              <Text variant="body">
                {t(`${TRANSLATION_KEY}.directReportsText`, {
                  count: recordCounts.directReports
                })}
              </Text>
              <div className="flex items-center justify-between gap-4">
                <Text variant="body" className="min-w-0">
                  {t(`${TRANSLATION_KEY}.newReportingManager`)}
                </Text>
                <div className="w-[314px]">
                  <SingleSelect
                    fieldName="new-reporting-manager"
                    options={availableUsers}
                    selectedValue={transferData.newReportingManager || ""}
                    onSelection={(value) =>
                      handleTransferChange(
                        "newReportingManager",
                        value as string
                      )
                    }
                    placeholder={t(`${TRANSLATION_KEY}.select`)}
                    variant="form"
                    showSearch={true}
                    isOpen={isManagerOpen}
                    onOpenChange={handleManagerOpenChange}
                    onSearch={setManagerSearchTerm}
                    isLoading={isLoadingUsers}
                    className="w-full"
                    renderIcon={renderUserIcon}
                    popoverClassName="w-[314px]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondaryNoBg" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            destructive
            onClick={handleDeactivate}
            disabled={!isFormValid}
          >
            {t(`${TRANSLATION_KEY}.deactivateUsers`)}
          </Button>
        </div>
      }
      contentClassname="max-w-2xl"
      hideDivider={true}
    />
  );
};

export default BulkDeactivateUsersModal;
